import { forwardRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { FeedbackModal } from '../features/FeedbackModal';
import { EditProfileModal } from '../features/EditProfileModal';
import { useI18n } from '../../i18n/I18nContext';

function LanguageToggle() {
  const { locale, setLocale } = useI18n();
  return (
    <div className="flex items-center justify-between px-3 py-3 rounded-md min-h-[48px]">
      <div className="flex items-center gap-3">
        <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
        </svg>
        <span className="text-base text-text-primary">Français</span>
      </div>
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={locale === 'fr'}
          onChange={(e) => setLocale(e.target.checked ? 'fr' : 'en')}
          className="sr-only peer"
          aria-label="Toggle French Canadian"
        />
        <div className="w-11 h-6 bg-neutral-200 dark:bg-neutral-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 dark:after:border-neutral-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 dark:peer-checked:bg-primary-500"></div>
      </label>
    </div>
  );
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

interface User {
  firstName: string;
  lastName: string;
  role: string;
}

interface MvpSidecarMenuProps {
  isOpen: boolean;
  onToggle: () => void;
  user: User;
  navItems: NavItem[];
  onThemeToggle?: (isDark: boolean) => void;
  isDarkMode?: boolean;
  className?: string;
}

export const MvpSidecarMenu = forwardRef<HTMLElement, MvpSidecarMenuProps>(
  ({ isOpen, onToggle, user, navItems, onThemeToggle, isDarkMode = false, className }, ref) => {
    const location = useLocation();
    const isActive = (path: string) => location.pathname.startsWith(path);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const { t } = useI18n();

    return (
      <>
        <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
        <EditProfileModal isOpen={profileOpen} onClose={() => setProfileOpen(false)} />
        {isOpen && (
          <div
            className="fixed inset-0 z-[1040] bg-black bg-opacity-50 backdrop-blur-sm transition-all duration-[300ms]"
            onClick={onToggle}
            aria-hidden="true"
          />
        )}

        <aside
          ref={ref}
          className={clsx(
            'fixed inset-y-0 right-0 z-[1050]',
            'w-full sm:w-[360px] max-w-full',
            'bg-white/80 dark:bg-neutral-800/80 backdrop-blur-lg shadow-xl',
            'transform transition-transform duration-[300ms] ease-in-out',
            isOpen ? 'translate-x-0' : 'translate-x-full',
            'flex flex-col overflow-hidden',
            className
          )}
          aria-label="Navigation menu"
          aria-hidden={!isOpen}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-5 border-b border-border-primary bg-white/60 dark:bg-neutral-800/60 backdrop-blur-md">
            <div>
              <h2 className="text-2xl font-bold text-text-primary">{t('nav.menu')}</h2>
              <p className="text-sm text-text-secondary mt-0.5">{user.firstName} {user.lastName}</p>
            </div>
            <button
              type="button"
              onClick={onToggle}
              className={clsx(
                'rounded-full p-3 min-w-[48px] min-h-[48px]',
                'text-text-secondary hover:text-text-primary hover:bg-surface-tertiary',
                'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-primary-500 focus-visible:outline-offset-2',
                'transition-all duration-[200ms]'
              )}
              aria-label="Close menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-5">
            {/* Navigation */}
            <section className="mb-8">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-text-secondary mb-3">
                {t('nav.navigation')}
              </h3>
              <nav className="space-y-1">
                {navItems.filter(item => item.id !== 'my-business').map((item) => {
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      onClick={onToggle}
                      className={clsx(
                        'flex items-center gap-3 px-3 py-3 rounded-md min-h-[48px]',
                        'text-base transition-all duration-[200ms]',
                        active
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                          : 'text-text-primary hover:bg-surface-tertiary'
                      )}
                    >
                      <span className={clsx('w-5 h-5', active ? 'text-primary-500 dark:text-primary-400' : 'text-text-secondary')}>
                        {item.icon}
                      </span>
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </section>

            {/* Experimental - secondary section */}
            <section className="mb-8 pt-4 border-t border-neutral-200 dark:border-neutral-700">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-orange-500/70 dark:text-orange-400/70 mb-3">
                Experimental
              </h3>
              <nav className="space-y-0.5">
                {navItems.filter(item => item.id === 'my-business').map((item) => {
                  const active = isActive(item.path);
                  return (
                    <Link key={item.id} to={item.path} onClick={onToggle} className={clsx('flex items-center gap-3 px-3 py-2.5 rounded-md min-h-[40px]', 'text-sm transition-all duration-[200ms]', active ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' : 'text-neutral-600 dark:text-neutral-400 hover:bg-surface-tertiary hover:text-text-primary')}>
                      <span className={clsx('w-4 h-4', active ? 'text-primary-500 dark:text-primary-400' : 'text-neutral-400')}>{item.icon}</span>
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
                <Link to="/mywfg-demo" onClick={onToggle} className={clsx('flex items-center gap-3 px-3 py-2.5 rounded-md min-h-[40px]', 'text-sm transition-all duration-[200ms]', isActive('/mywfg-demo') ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' : 'text-neutral-600 dark:text-neutral-400 hover:bg-surface-tertiary hover:text-text-primary')}>
                  <span className={clsx('w-4 h-4', isActive('/mywfg-demo') ? 'text-primary-500' : 'text-neutral-400')}><svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg></span>
                  <span>MyWFG Demo</span>
                </Link>
                <Link to="/widget-config" onClick={onToggle} className={clsx('flex items-center gap-3 px-3 py-2.5 rounded-md min-h-[40px]', 'text-sm transition-all duration-[200ms]', isActive('/widget-config') ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' : 'text-neutral-600 dark:text-neutral-400 hover:bg-surface-tertiary hover:text-text-primary')}>
                  <span className={clsx('w-4 h-4', isActive('/widget-config') ? 'text-primary-500' : 'text-neutral-400')}><svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg></span>
                  <span>Edit Widgets</span>
                </Link>
                <Link to="/reports" onClick={onToggle} className={clsx('flex items-center gap-3 px-3 py-2.5 rounded-md min-h-[40px]', 'text-sm transition-all duration-[200ms]', isActive('/reports') ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' : 'text-neutral-600 dark:text-neutral-400 hover:bg-surface-tertiary hover:text-text-primary')}>
                  <span className={clsx('w-4 h-4', isActive('/reports') ? 'text-primary-500' : 'text-neutral-400')}><svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg></span>
                  <span>Report Generator</span>
                </Link>
                <Link to="/hero-card" onClick={onToggle} className={clsx('flex items-center gap-3 px-3 py-2.5 rounded-md min-h-[40px]', 'text-sm transition-all duration-[200ms]', isActive('/hero-card') ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' : 'text-neutral-600 dark:text-neutral-400 hover:bg-surface-tertiary hover:text-text-primary')}>
                  <span className={clsx('w-4 h-4', isActive('/hero-card') ? 'text-primary-500' : 'text-neutral-400')}><svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg></span>
                  <span>Hero Card</span>
                </Link>
                <Link to="/agent-pages" onClick={onToggle} className={clsx('flex items-center gap-3 px-3 py-2.5 rounded-md min-h-[40px]', 'text-sm transition-all duration-[200ms]', isActive('/agent-pages') ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' : 'text-neutral-600 dark:text-neutral-400 hover:bg-surface-tertiary hover:text-text-primary')}>
                  <span className={clsx('w-4 h-4', isActive('/agent-pages') ? 'text-primary-500' : 'text-neutral-400')}><svg fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" /></svg></span>
                  <span>Agent Pages</span>
                </Link>
              </nav>
            </section>

            {/* Feedback */}
            <section className="mb-8">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-text-secondary mb-3">
                {t('nav.settings')}
              </h3>
              <button
                onClick={() => {
                  setProfileOpen(true);
                  onToggle();
                }}
                className={clsx(
                  'w-full flex items-center gap-3 px-3 py-3 rounded-md min-h-[48px]',
                  'text-base text-text-primary hover:bg-surface-tertiary',
                  'transition-all duration-base'
                )}
              >
                <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>{t('nav.editProfile')}</span>
              </button>
              <button
                onClick={() => { setFeedbackOpen(true); onToggle(); }}
                className={clsx(
                  'w-full flex items-center gap-3 px-3 py-3 rounded-md min-h-[48px]',
                  'text-base text-text-primary hover:bg-surface-tertiary',
                  'transition-all duration-base'
                )}
              >
                <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                <span>{t('nav.sendFeedback')}</span>
              </button>
            </section>

            {/* Theme Toggle */}
            <section className="mb-8">
              <div className="flex items-center justify-between px-3 py-3 rounded-md min-h-[48px]">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="5" />
                    <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                    <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                  </svg>
                  <span className="text-base text-text-primary">{t('nav.darkMode')}</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isDarkMode}
                    onChange={(e) => onThemeToggle?.(e.target.checked)}
                    className="sr-only peer"
                    aria-label="Toggle dark mode"
                  />
                  <div className="w-11 h-6 bg-neutral-200 dark:bg-neutral-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 dark:after:border-neutral-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 dark:peer-checked:bg-primary-500"></div>
                </label>
              </div>
              {/* Language Toggle */}
              <LanguageToggle />
            </section>
          </div>

          {/* Exit to MyWFG — pinned to bottom */}
          <div className="px-5 py-4 border-t border-border-primary bg-white/60 dark:bg-neutral-800/60 backdrop-blur-md">
            <a
              href="http://localhost:3001/legacy-original.html"
              className={clsx(
                'flex items-center justify-center gap-2 w-full px-4 py-3 rounded-lg min-h-[48px]',
                'bg-neutral-100 dark:bg-neutral-700 text-text-primary',
                'hover:bg-neutral-200 dark:hover:bg-neutral-600',
                'font-semibold text-base transition-all duration-[200ms]',
                'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-primary-500 focus-visible:outline-offset-2'
              )}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
              </svg>
              {t('nav.exitToMyWFG')}
            </a>
          </div>
        </aside>
      </>
    );
  }
);

MvpSidecarMenu.displayName = 'MvpSidecarMenu';
